import { createAsyncThunk } from "@reduxjs/toolkit";

import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { GetAllNotifications, MarkAsRead, CreateNotificationForAll, DeleteNotification, BulkDeleteNotifications } from "helpers/api_method";
import { APIClient } from "helpers/api_helper";

export const getAllNotificationList = createAsyncThunk(
    "notification/getAllNotificationList", 
    async (params: { page?: number; per_page?: number; unread_only?: boolean } = {}, { rejectWithValue }) => {
        try {
            // Build query parameters
            const queryParams: any = {};
            if (params?.page) queryParams.page = params.page.toString();
            if (params?.per_page) queryParams.per_page = params.per_page.toString();
            if (params?.unread_only) queryParams.unread_only = 'true';

            const api = new APIClient();
            const data = await api.get('/api/notifications/', queryParams);
            
            // Check if response indicates user is inactive
            if (data && data.data && data.data.status === 'inactive') {
                // Import and use UserStatusService to handle inactive user
                const UserStatusService = (await import('services/userStatusService')).default;
                const statusService = UserStatusService.getInstance();
                statusService.handleInactiveUser(data.data);
                return rejectWithValue('User account is inactive');
            }
            
            // Handle different response structures for active users
            let notifications = [];
            
            if (data && data.data) {
                // New format with status and notifications
                if (data.data.status === 'active' && data.data.notifications) {
                    notifications = Array.isArray(data.data.notifications) ? data.data.notifications : [];
                } else if (Array.isArray(data.data)) {
                    // Legacy format - direct array
                    notifications = data.data;
                } else {
                    notifications = [];
                }
            } else if (Array.isArray(data)) {
                // If response itself is an array
                notifications = data;
            } else {
                // Default to empty array
                notifications = [];
            }
            
            // Return enhanced response with pagination info
            return {
                notifications: Array.isArray(notifications) ? notifications : [],
                pagination: data.data?.pagination || null,
                stats: data.data?.stats || null
            };
            
        } catch (error: any) {
            // Handle specific error cases for inactive users
            if (error.response?.status === 403 || error.response?.status === 404) {
                const UserStatusService = (await import('services/userStatusService')).default;
                const statusService = UserStatusService.getInstance();
                statusService.handleInactiveUser(error.response.data);
                return rejectWithValue('User account is inactive');
            }
            
            return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to fetch notifications');
        }
    }
);

export const getUnreadNotifications = createAsyncThunk(
    "notification/getUnreadNotifications", 
    async (params: { page?: number; per_page?: number } = {}, { rejectWithValue }) => {
        try {
            // Build query parameters
            const queryParams: any = {};
            if (params?.page) queryParams.page = params.page.toString();
            if (params?.per_page) queryParams.per_page = params.per_page.toString();

            const api = new APIClient();
            const data = await api.get('/api/notifications/unread', queryParams);
            
            let notifications = data.data?.notifications || [];
            
            return {
                notifications: Array.isArray(notifications) ? notifications : [],
                pagination: data.data?.pagination || null
            };
            
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to fetch unread notifications');
        }
    }
);

export const getNotificationStats = createAsyncThunk("notification/getNotificationStats", async (_, { rejectWithValue }) => {
    try {
        const api = new APIClient();
        const data = await api.get('/api/notifications/stats');
        return data.data || {};
        
    } catch (error: any) {
        return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to fetch notification statistics');
    }
});

export const updateExistingNotification = createAsyncThunk("notification/updateExistingNotification", async (data: any, { rejectWithValue }) => {
    try {
        const response = await MarkAsRead(data.id, data);
        toast.success("Notification updated successfully", { autoClose: 2000 });
        return response.data;
    } catch (error: any) {
        toast.error("Failed to update notification", { autoClose: 2000 });
        return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to update notification');
    }
});

export const markAllNotificationsAsRead = createAsyncThunk("notification/markAllNotificationsAsRead", async (_, { rejectWithValue }) => {
    try {
        const api = new APIClient();
        const data = await api.put('/api/notifications/mark-all-read', {});
        toast.success("All notifications marked as read", { autoClose: 2000 });
        return data.data;
        
    } catch (error: any) {
        toast.error("Failed to mark all notifications as read", { autoClose: 2000 });
        return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to mark all notifications as read');
    }
});

export const createNotificationForAll = createAsyncThunk("notification/createNotificationForAll", async (data: any, { rejectWithValue }) => {
    try {
        const response = await CreateNotificationForAll(data);
        toast.success("Notification created for all users successfully", { autoClose: 2000 });
        return response.data;
    } catch (error: any) {
        toast.error("Failed to create notification", { autoClose: 2000 });
        return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to create notification');
    }
});

export const deleteNotification = createAsyncThunk("notification/deleteNotification", async (id: string, { rejectWithValue }) => {
    try {
        const response = await DeleteNotification(id);
        toast.success("Notification deleted successfully", { autoClose: 2000 });
        return response.data;
    } catch (error: any) {
        toast.error("Failed to delete notification", { autoClose: 2000 });
        return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to delete notification');
    }
});

export const bulkDeleteNotifications = createAsyncThunk("notification/bulkDeleteNotifications", async (ids: string[], { rejectWithValue }) => {
    try {
        const response = await BulkDeleteNotifications(ids);
        toast.success("Notifications deleted successfully", { autoClose: 2000 });
        return response.data;
    } catch (error: any) {
        toast.error("Failed to delete notifications", { autoClose: 2000 });
        return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to delete notifications');
    }
});


export const refreshNotifications = createAsyncThunk("notification/refreshNotifications", async (_, { dispatch }) => {
    try {
        // Refresh notification polling service
        const NotificationPollingService = (await import('services/notificationPollingService')).default;
        const notificationService = NotificationPollingService.getInstance();
        await notificationService.refreshNotifications();
        
        // Also refresh the notification list
        await dispatch(getAllNotificationList({ per_page: 1000 }));
        
        return { success: true };
    } catch (error: any) {
        return { success: false, error: error.message };
    }
});

export const getNotificationPreferences = createAsyncThunk("notification/getNotificationPreferences", async (_, { rejectWithValue }) => {
    try {
        const api = new APIClient();
        const data = await api.get('/api/notifications/preferences');
        return data.data || {};
    } catch (error: any) {
        return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to fetch notification preferences');
    }
});

export const updateNotificationPreferences = createAsyncThunk("notification/updateNotificationPreferences", async (preferences: any, { rejectWithValue }) => {
    try {
        const api = new APIClient();
        const data = await api.put('/api/notifications/preferences', preferences);
        toast.success("Notification preferences updated successfully", { autoClose: 2000 });
        return data.data || {};
    } catch (error: any) {
        toast.error("Failed to update notification preferences", { autoClose: 2000 });
        return rejectWithValue(error?.response?.data?.message || error?.message || 'Failed to update notification preferences');
    }
});